import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInvalidateAnalysisRuns } from '@/lib/analyses/useAnalysisRuns';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useId, useMemo } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { groundClassifyTrackingAtom } from '@/atoms/groundClassify-progress.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { execute } from '@/graphql/execute';
import type { LayersQuery } from '@/graphql/graphql';
import { useDialog } from '@/providers/DialogProvider';
import { startGroundClassification } from '@/queries/groundClassify.query';
import { isPointcloudLayer } from '@/lib/analyses/candidates';
import { useSourceLayerPick } from '@/lib/analyses/useSourceLayerPick';
import {
  AddOnToggle,
  AdvancedSection,
  AnalysisSection,
  FormInputRoleField,
  InputRoleField,
  OutputSummary,
} from '@/components/analyses/sections';

import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AnalysisDialogShell } from '@/components/analyses/AnalysisDialogShell';

type LayerData = LayersQuery['layers'][number];

interface Props {
  /** Pre-fill for the source picker (context layer, re-run). D10. */
  sourceLayer?: LayerData;
  projectId: string;
}

// Colour ramps mirrored from scripts/las2ground.py's _RAMPS dict. Keep this
// list in sync — the worker tolerates unknown names (falls back to viridis)
// but the dropdown should only offer values that actually render correctly.
const COLORMAPS = ['viridis', 'cividis', 'magma', 'terrain', 'greyscale'] as const;
type Colormap = (typeof COLORMAPS)[number];

/** The fields under "Advanced settings" — a validation error on any of them
 *  opens the section. */
const ADVANCED_FIELDS = [
  'runElm',
  'runOutlier',
  'resetClassification',
  'classifyAllReturns',
  'smrfCell',
  'smrfScalar',
  'smrfSlope',
  'smrfThreshold',
  'smrfWindow',
  'smrfCut',
  'csfResolution',
  'csfThreshold',
  'csfHdiff',
  'csfSmooth',
  'csfStep',
  'csfRigidness',
  'csfIterations',
] as const;

/** The local `NumberField` this dialog used to carry parsed its two integer
 *  fields with `parseInt`; the shared `NumericField` parses floats, so the
 *  integer fields truncate the same way before the value reaches the form. */
const toInt = (v: number) => Math.trunc(v);

interface FormData {
  name: string;
  boundaryLayerId: string;
  algorithm: 'smrf' | 'csf';
  resolution: number;
  optimizePointCloud: boolean;
  rasterOutputType: 'idw' | 'mean' | 'min' | 'max';
  // Pre-processing toggles
  runElm: boolean;
  runOutlier: boolean;
  resetClassification: boolean;
  classifyAllReturns: boolean;
  // Optional outputs
  writeCount: boolean;
  writeRgbDtm: boolean;
  colormap: Colormap;
  // SMRF
  smrfCell: number;
  smrfScalar: number;
  smrfSlope: number;
  smrfThreshold: number;
  smrfWindow: number;
  smrfCut: number;
  // CSF
  csfResolution: number;
  csfThreshold: number;
  csfHdiff: number;
  csfSmooth: boolean;
  csfStep: number;
  csfRigidness: number;
  csfIterations: number;
}

/**
 * Dialog for kicking off a ground-classification job from an existing
 * POINTCLOUD layer's context menu. Mirrors RunCompareSurfacesDialog: form
 * → typed mutation → synthetic batch → preprocess → ECS worker.
 *
 * Body follows the shared config sections (Inputs → Settings → Add-ons →
 * Output → Advanced settings). The full SMRF/CSF parameter surface sits in
 * the collapsed Advanced settings so first-time users see only the
 * essentials (algorithm + resolution) and power users can drill into the
 * tuning knobs without leaving the dialog.
 */
export function RunGroundClassificationDialog({ sourceLayer: initialSourceLayer, projectId }: Props) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const formId = useId();
  const queryClient = useQueryClient();
  const [, setTrackers] = useAtom(groundClassifyTrackingAtom);

  const allLayers = useAtomValue(layersAtom);
  const {
    candidates: sourceCandidates,
    sourceLayer,
    sourceLayerId,
    setSourceLayerId,
  } = useSourceLayerPick(isPointcloudLayer, initialSourceLayer);
  const boundaryCandidates = useMemo(
    () => allLayers.filter((l) => l.type === 'VECTOR' && l.id !== sourceLayerId),
    [allLayers, sourceLayerId],
  );

  const formSchema = z.object({
    name: z.string().min(1),
    boundaryLayerId: z.string().default(''),
    algorithm: z.enum(['smrf', 'csf']),
    resolution: z.number().positive(),
    optimizePointCloud: z.boolean(),
    rasterOutputType: z.enum(['idw', 'mean', 'min', 'max']),
    runElm: z.boolean(),
    runOutlier: z.boolean(),
    resetClassification: z.boolean(),
    classifyAllReturns: z.boolean(),
    writeCount: z.boolean(),
    writeRgbDtm: z.boolean(),
    colormap: z.enum(COLORMAPS),
    smrfCell: z.number().positive(),
    smrfScalar: z.number().positive(),
    smrfSlope: z.number().min(0),
    smrfThreshold: z.number().positive(),
    smrfWindow: z.number().positive(),
    smrfCut: z.number().min(0),
    csfResolution: z.number().positive(),
    csfThreshold: z.number().positive(),
    csfHdiff: z.number().min(0),
    csfSmooth: z.boolean(),
    csfStep: z.number().positive(),
    csfRigidness: z.number().int().min(1).max(3),
    csfIterations: z.number().int().min(50),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    // Defaults match scripts/las2ground.py CLI defaults so a power user's
    // CLI first-pass values match the dialog's first-pass values.
    defaultValues: {
      name: `${sourceLayer?.name ?? ''} DTM`.trim(),
      boundaryLayerId: '',
      algorithm: 'smrf',
      resolution: 1.0,
      optimizePointCloud: true,
      // Default `mean` is the fast path: numpy 2-D bincount instead of
      // PDAL's single-threaded IDW writer. Equivalent quality on dense
      // ground clouds (which is the typical input here); users who
      // need real interpolation for sparse / gappy data switch to IDW.
      rasterOutputType: 'mean',
      runElm: false,
      runOutlier: false,
      resetClassification: false,
      classifyAllReturns: false,
      writeCount: false,
      // RGB DTM companion is on by default — without it the DTM is
      // download-only because gdal2tiles refuses Float32 input.
      writeRgbDtm: true,
      // Default colormap. viridis is perceptually uniform and
      // colourblind-safe, which matters for the "tile shown on a 2D
      // map" use-case. Hypsometric `terrain` is available for users
      // who specifically want the topographic blue→white look.
      colormap: 'viridis',
      smrfCell: 1.0,
      smrfScalar: 1.2,
      smrfSlope: 0.2,
      smrfThreshold: 0.45,
      smrfWindow: 16.0,
      smrfCut: 0.0,
      csfResolution: 1.0,
      csfThreshold: 0.5,
      csfHdiff: 0.3,
      csfSmooth: true,
      csfStep: 0.65,
      csfRigidness: 3,
      csfIterations: 500,
    },
  });

  const watchedAlgorithm = form.watch('algorithm');
  // Live values for the Output section's "Will create" line.
  const [watchedName, writeRgbDtm, writeCount] = useWatch({
    control: form.control,
    name: ['name', 'writeRgbDtm', 'writeCount'],
  });
  // Opens Advanced settings by itself when a field inside it fails validation.
  const { errors } = useFormState({ control: form.control });
  const advancedHasError = ADVANCED_FIELDS.some((f) => !!errors[f]);

  // The suggested output name follows the picked source until the user types one.
  useEffect(() => {
    if (!sourceLayer || form.formState.dirtyFields.name) return;
    const suggested = `${sourceLayer.name} DTM`;
    if (form.getValues('name') !== suggested) form.setValue('name', suggested, { shouldDirty: false });
  }, [sourceLayer, form]);

  const invalidateAnalysisRuns = useInvalidateAnalysisRuns();
  const mutation = useMutation({
    mutationFn: async ({ vars, source }: { vars: FormData; source: LayerData }) => {
      return execute(startGroundClassification, {
        input: {
          sourceLayerId: source.id,
          name: vars.name || undefined,
          boundaryLayerId: vars.boundaryLayerId || undefined,
          params: {
            algorithm: vars.algorithm,
            resolution: vars.resolution,
            optimizePointCloud: vars.optimizePointCloud,
            rasterOutputType: vars.rasterOutputType,
            runElm: vars.runElm,
            runOutlier: vars.runOutlier,
            resetClassification: vars.resetClassification,
            classifyAllReturns: vars.classifyAllReturns,
            writeCount: vars.writeCount,
            writeRgbDtm: vars.writeRgbDtm,
            colormap: vars.colormap,
            smrfCell: vars.smrfCell,
            smrfScalar: vars.smrfScalar,
            smrfSlope: vars.smrfSlope,
            smrfThreshold: vars.smrfThreshold,
            smrfWindow: vars.smrfWindow,
            smrfCut: vars.smrfCut,
            csfResolution: vars.csfResolution,
            csfThreshold: vars.csfThreshold,
            csfHdiff: vars.csfHdiff,
            csfSmooth: vars.csfSmooth,
            csfStep: vars.csfStep,
            csfRigidness: vars.csfRigidness,
            csfIterations: vars.csfIterations,
          },
        },
      });
    },
    onSuccess: (data, { source }) => {
      invalidateAnalysisRuns(projectId);
      const batchId = data.startGroundClassification;
      // Open a long-lived loading toast (`duration: Infinity` — won't
      // auto-dismiss) and hand it to the project-shell tracker hook.
      // The hook resolves it into either a success or a failure toast
      // when the run actually finishes server-side, so the user's
      // visible state matches reality (mirrors `useDeviationProgress`).
      const toastId = toast.loading(t('groundClassification.runningToast', { name: source.name }), {
        duration: Infinity,
      });
      // Snapshot existing layers derived from this source so the hook
      // can detect "the new one this run produces" by exclusion. Same
      // disambiguation strategy deviation uses; survives client/server
      // clock skew that breaks naive timestamp comparisons.
      const cachedLayers = queryClient.getQueryData<{ layers: LayerData[] }>(['layers', projectId]);
      const existingDerivedLayerIds = (cachedLayers?.layers ?? [])
        .filter((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string };
          return meta.derivedFrom === source.id;
        })
        .map((l) => l.id);
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: source.id,
          sourceLayerName: source.name,
          existingDerivedLayerIds,
        },
      ]);
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('groundClassification.failedToStart'));
    },
  });

  return (
    <AnalysisDialogShell
      title={t('groundClassification.title')}
      formId={formId}
      primaryLabel={mutation.isPending ? t('groundClassification.starting') : t('groundClassification.run')}
      primaryDisabled={mutation.isPending || !sourceLayer}
      primaryBusy={mutation.isPending}
      primaryTestId='ground-run'
    >
      <Form {...form}>
        {/* No height cap here: the shell owns it (ANALYSIS_SHELL_CLASS). */}
        <form
          id={formId}
          onSubmit={form.handleSubmit((vars) => {
            if (!sourceLayer) return;
            mutation.mutate({ vars: vars as unknown as FormData, source: sourceLayer });
          })}
        >
          <div className='flex min-w-0 flex-col gap-4 px-1 py-1'>
            <AnalysisSection title={t('analysisConfig.sections.inputs')} testId='ground-section-inputs'>
              <InputRoleField
                id='ground-source-layer'
                testId='ground-source-layer'
                role='asBuilt'
                label={t('analysisConfig.roles.surveyPointcloud')}
                layers={sourceCandidates}
                value={sourceLayerId}
                onChange={setSourceLayerId}
                disabled={mutation.isPending}
              />
              <FormInputRoleField
                control={form.control}
                name='boundaryLayerId'
                role='boundary'
                optional
                testId='ground-boundary'
                layers={boundaryCandidates}
                help={t('boundary.help')}
              />
            </AnalysisSection>

            <AnalysisSection title={t('analysisConfig.sections.settings')} testId='ground-section-settings'>
              {/* Algorithm — radio (not select) because there are only two
                  options and they have meaningfully different semantics, so
                  a radio group with descriptions reads better. */}
              <FormField
                control={form.control}
                name='algorithm'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('groundClassification.algorithm')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className='flex flex-col gap-2'
                      >
                        {(['smrf', 'csf'] as const).map((algo) => (
                          <label key={algo} className='flex cursor-pointer items-start gap-2'>
                            <RadioGroupItem value={algo} id={`algo-${algo}`} />
                            <div>
                              <div className='text-sm font-medium'>
                                {t(`groundClassification.algorithms.${algo}.label`)}
                              </div>
                              <div className='text-xs text-slate-600 dark:text-stone-400'>
                                {t(`groundClassification.algorithms.${algo}.description`)}
                              </div>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <FormField
                  control={form.control}
                  name='resolution'
                  render={({ field }) => (
                    <FormItem className='min-w-0 self-start'>
                      <FormLabel>{t('groundClassification.resolution')}</FormLabel>
                      <FormControl>
                        <NumericField id='ground-resolution' value={field.value} onChange={field.onChange} />
                      </FormControl>
                      <FormDescription>{t('groundClassification.resolutionHelp')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='rasterOutputType'
                  render={({ field }) => (
                    <FormItem className='min-w-0 self-start'>
                      <FormLabel>{t('groundClassification.rasterOutputType')}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className='w-full'>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value='idw'>IDW</SelectItem>
                          <SelectItem value='mean'>Mean</SelectItem>
                          <SelectItem value='min'>Min</SelectItem>
                          <SelectItem value='max'>Max</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>{t('groundClassification.rasterOutputTypeHelp')}</FormDescription>
                    </FormItem>
                  )}
                />
              </div>
            </AnalysisSection>

            <AnalysisSection title={t('analysisConfig.sections.addOns')} testId='ground-section-addons'>
              {/* Coloured DTM sits outside Advanced because turning it off has
                  user-visible consequences (no terrain layer in the viewer).
                  Its colour ramp only shows while it is on — picking a ramp
                  is meaningless without the RGB output. */}
              <AddOnToggle
                id='ground-rgb-dtm'
                testId='ground-rgb-dtm'
                label={t('analysisConfig.addOns.colouredDtm')}
                help={t('groundClassification.writeRgbDtmHelp')}
                checked={writeRgbDtm}
                onCheckedChange={(c) => form.setValue('writeRgbDtm', c, { shouldDirty: true })}
              >
                <FormField
                  control={form.control}
                  name='colormap'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('groundClassification.colormap')}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COLORMAPS.map((cm) => (
                            <SelectItem key={cm} value={cm}>
                              {t(`groundClassification.colormaps.${cm}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>{t('groundClassification.colormapHelp')}</FormDescription>
                    </FormItem>
                  )}
                />
              </AddOnToggle>
              <AddOnToggle
                id='ground-write-count'
                testId='ground-write-count'
                label={t('analysisConfig.addOns.countRaster')}
                checked={writeCount}
                onCheckedChange={(c) => form.setValue('writeCount', c, { shouldDirty: true })}
              />
            </AnalysisSection>

            <AnalysisSection title={t('analysisConfig.sections.output')} testId='ground-section-output'>
              <OutputSummary
                testId='ground-output'
                creates={[
                  t('analysisConfig.creates.groundPointcloud', {
                    name: watchedName || '…',
                    interpolation: { escapeValue: false },
                  }),
                  t('analysisConfig.creates.dtmGeotiff'),
                  writeRgbDtm && t('analysisConfig.creates.colouredDtm'),
                  writeCount && t('analysisConfig.creates.countRaster'),
                ]}
              >
                <FormField
                  control={form.control}
                  name='optimizePointCloud'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('groundClassification.pointCloudDensity')}</FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value ? 'optimized' : 'full'}
                          onValueChange={(value) => field.onChange(value === 'optimized')}
                          className='grid gap-2 sm:grid-cols-2'
                        >
                          {(['optimized', 'full'] as const).map((option) => {
                            const selected = field.value === (option === 'optimized');
                            return (
                              <label
                                key={option}
                                htmlFor={`ground-density-${option}`}
                                className={`flex cursor-pointer items-start gap-2.5 rounded-md border p-3 text-sm transition-colors ${
                                  selected
                                    ? 'border-sky-500 bg-sky-50/70 dark:border-sky-500 dark:bg-sky-950/30'
                                    : 'border-slate-200 hover:border-slate-300 dark:border-stone-700 dark:hover:border-stone-500'
                                }`}
                              >
                                <RadioGroupItem
                                  value={option}
                                  id={`ground-density-${option}`}
                                  className='mt-0.5 shrink-0'
                                />
                                <span className='min-w-0'>
                                  <span className='block font-medium text-slate-900 dark:text-stone-100'>
                                    {t(`groundClassification.density.${option}.label`)}
                                  </span>
                                  <span className='mt-1 block text-xs leading-relaxed text-slate-600 dark:text-stone-400'>
                                    {t(`groundClassification.density.${option}.description`)}
                                  </span>
                                </span>
                              </label>
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                      <FormDescription>{t('groundClassification.pointCloudDensityHelp')}</FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('analysisConfig.resultName')}</FormLabel>
                      <FormControl>
                        <Input type='text' data-testid='ground-name' {...field} />
                      </FormControl>
                      <FormDescription>{t('groundClassification.outputNameHelp')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </OutputSummary>
            </AnalysisSection>

            <AdvancedSection testId='ground-advanced' hasError={advancedHasError}>
              {/* Pre-processing block. Each toggle gets a one-line
                  FormDescription so users tuning a noisy scan can read
                  why each pre-filter exists without leaving the dialog.
                  Block-level intro at the top frames the category. */}
              <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                <div>
                  <FormLabel className='text-sm font-medium'>{t('groundClassification.preprocessing')}</FormLabel>
                  <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                    {t('groundClassification.preprocessingHelp')}
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name='runElm'
                  render={({ field }) => (
                    <FormItem>
                      <div className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('groundClassification.runElm')}</FormLabel>
                      </div>
                      <FormDescription>{t('groundClassification.runElmHelp')}</FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='runOutlier'
                  render={({ field }) => (
                    <FormItem>
                      <div className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('groundClassification.runOutlier')}</FormLabel>
                      </div>
                      <FormDescription>{t('groundClassification.runOutlierHelp')}</FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='resetClassification'
                  render={({ field }) => (
                    <FormItem>
                      <div className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('groundClassification.resetClassification')}</FormLabel>
                      </div>
                      <FormDescription>{t('groundClassification.resetClassificationHelp')}</FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='classifyAllReturns'
                  render={({ field }) => (
                    <FormItem>
                      <div className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('groundClassification.classifyAllReturns')}</FormLabel>
                      </div>
                      <FormDescription>{t('groundClassification.classifyAllReturnsHelp')}</FormDescription>
                    </FormItem>
                  )}
                />
              </div>

              {/* SMRF block. Active only when algorithm=smrf. The
                  intro paragraph at the top describes what SMRF is
                  doing internally so users tuning a knob have the
                  physical intuition; per-field FormDescription gives
                  the one-line "what does this knob do" reminder. */}
              {watchedAlgorithm === 'smrf' && (
                <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                  <div>
                    <FormLabel className='text-sm font-medium'>SMRF</FormLabel>
                    <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                      {t('groundClassification.smrfHelp')}
                    </p>
                  </div>
                  <div className='grid grid-cols-2 gap-3'>
                    {(['smrfCell', 'smrfScalar', 'smrfSlope', 'smrfThreshold', 'smrfWindow', 'smrfCut'] as const).map(
                      (name) => (
                        <FormField
                          key={name}
                          control={form.control}
                          name={name}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t(`groundClassification.${name}`)}</FormLabel>
                              <FormControl>
                                <NumericField id={`ground-${name}`} value={field.value} onChange={field.onChange} />
                              </FormControl>
                              <FormDescription>{t(`groundClassification.${name}Help`)}</FormDescription>
                            </FormItem>
                          )}
                        />
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* CSF block. Active only when algorithm=csf. Same shape
                  as the SMRF block above — intro paragraph framing the
                  cloth-simulation mental model, per-field
                  FormDescription on each knob. */}
              {watchedAlgorithm === 'csf' && (
                <div className='space-y-3 rounded-md border border-slate-200/70 p-3 dark:border-stone-700/40'>
                  <div>
                    <FormLabel className='text-sm font-medium'>CSF</FormLabel>
                    <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                      {t('groundClassification.csfHelp')}
                    </p>
                  </div>
                  <div className='grid grid-cols-2 gap-3'>
                    {(['csfResolution', 'csfThreshold', 'csfHdiff', 'csfStep'] as const).map((name) => (
                      <FormField
                        key={name}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t(`groundClassification.${name}`)}</FormLabel>
                            <FormControl>
                              <NumericField id={`ground-${name}`} value={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormDescription>{t(`groundClassification.${name}Help`)}</FormDescription>
                          </FormItem>
                        )}
                      />
                    ))}
                    <FormField
                      control={form.control}
                      name='csfRigidness'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('groundClassification.csfRigidness')}</FormLabel>
                          <FormControl>
                            <NumericField
                              id={`ground-${field.name}`}
                              value={field.value}
                              onChange={(v) => field.onChange(toInt(v))}
                              step='1'
                            />
                          </FormControl>
                          <FormDescription>{t('groundClassification.csfRigidnessHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='csfIterations'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('groundClassification.csfIterations')}</FormLabel>
                          <FormControl>
                            <NumericField
                              id={`ground-${field.name}`}
                              value={field.value}
                              onChange={(v) => field.onChange(toInt(v))}
                              step='1'
                            />
                          </FormControl>
                          <FormDescription>{t('groundClassification.csfIterationsHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name='csfSmooth'
                    render={({ field }) => (
                      <FormItem>
                        <div className='flex items-center gap-2'>
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <FormLabel className='!mt-0'>{t('groundClassification.csfSmooth')}</FormLabel>
                        </div>
                        <FormDescription>{t('groundClassification.csfSmoothHelp')}</FormDescription>
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </AdvancedSection>
          </div>
        </form>
      </Form>
    </AnalysisDialogShell>
  );
}
